<template>
	<div>
		<label>
			Manual create e-Coupon
		</label>
		<p class="typo-body-2 color-black-45 mb-4">
			Create code by importing file. Support .xls, xlsx with the maximum file of 10 MB
		</p>
		<CRow>
			<CCol>
				<CButton
					:disabled="isImporting || !hasEditPermission"
					class="transparent"
					color="secondary"
					data-test-id="import-coupon-code"
					@click="importCouponCode"
				>
					{{ isImporting ? 'Importing...' : 'Import .xlsx' }}
				</CButton>
			</CCol>
		</CRow>
		<CRow
			v-if="showCouponList"
			class="mt-4"
		>
			<CCol md="12">
				<p class="typo-body-2 mt-2 mb-2">
					Total {{ totalCouponCodes }} code(s)
				</p>
			</CCol>
			<CCol md="12">
				<div class="coupon-codes p-3">
					<p class="typo-caption-2 mb-0">{{ couponCodesText }}</p>
				</div>
			</CCol>
			<CCol v-if="!isSingleCode" md="12" class="mt-3">
				<div>
					<CouponBadge :label="couponCodePattern" />
					<span class="typo-caption-2">{{ `If it’s multi-code, the code will be displayed as "${couponCodePattern}" on storefront.` }}</span>
				</div>
			</CCol>
		</CRow>
	</div>
</template>

<script>
import { mapActions } from 'vuex';

import CouponBadge from '@/components/CouponBadge.vue';

import { importCouponCodesAPI } from '../services/api/promotions.api';

import {
	MAXIMUM_COUPON_IMPORT_FILE_SIZE,
	MAXIMUM_COUPON_IMPORT_FILE_SIZE_LABEL,
} from '../enums/promotions';

import { getECouponCodePattern, getECouponCodesPreviewText } from '../assets/js/helpers';
import isHavePermission from '../mixins/isHavePermission';

export default {
	name: 'PromotionECouponManual',

	components: {
		CouponBadge,
	},

	mixins: [isHavePermission],

	props: {
		dataECoupon: {
			type: Object,
			default: null,
		},
		isEditMode: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		const { couponCodes = [], quantity = null } = this.dataECoupon || {};

		return {
			isImporting: false,
			// edit mode: API ส่ง `codes` มาแค่ตัวอย่าง (สูงสุด 10) จำนวนจริงคือ `code.quantity`
			// เก็บค่าตอนเปิดหน้าไว้ เพราะ `dataECoupon.quantity` จะถูกทับด้วยค่าที่ emit ขึ้นไป
			existingQuantity: this.isEditMode ? (Number(quantity) || couponCodes.length) : 0,
			formData: {
				couponCodes,
				newCouponCodes: [],
			},
		};
	},

	computed: {
		mergeCouponCodes() {
			if (Array.isArray(this.formData.couponCodes) && Array.isArray(this.formData.newCouponCodes)) {
				return [...new Set([...this.formData.couponCodes, ...this.formData.newCouponCodes])];
			}

			return [];
		},
		// โค้ดใหม่ที่ไม่ซ้ำกับตัวอย่างที่มีอยู่ (และไม่ซ้ำกันเอง)
		uniqueNewCouponCodes() {
			const existingCodes = new Set(this.formData.couponCodes || []);
			return [...new Set(this.formData.newCouponCodes || [])].filter((code) => !existingCodes.has(code));
		},
		couponCodesText() {
			return getECouponCodesPreviewText(this.mergeCouponCodes, this.totalCouponCodes);
		},
		codePrefix() {
			if (!this.formData.couponCodes || !Array.isArray(this.formData.couponCodes)) {
				return null;
			}

			// First 3 charecters will be Prefix value
			const defaultPrefixLength = 3;
			const firstCouponCode = this.formData.couponCodes[0];

			if (!firstCouponCode) {
				return null;
			}

			// If code length longer than default, substring
			// If not, use the code to be prefix
			return firstCouponCode.length > defaultPrefixLength
				? firstCouponCode.substring(0, defaultPrefixLength)
				: firstCouponCode;
		},
		showCouponList() {
			return this.totalCouponCodes > 0;
		},
		// ห้ามนับจาก array ตอน edit — `codes` จาก API มีแค่ตัวอย่าง
		totalCouponCodes() {
			if (this.isEditMode) {
				return this.existingQuantity + this.uniqueNewCouponCodes.length;
			}

			return this.mergeCouponCodes.length;
		},
		isSingleCode() {
			return this.totalCouponCodes === 1;
		},
		codeSuffixLength() {
			if (this.totalCouponCodes > 0 && this.codePrefix) {
				return (this.mergeCouponCodes[0].length - this.codePrefix.length);
			}

			return 0;
		},
		couponCodePattern() {
			return getECouponCodePattern(this.codePrefix, this.codeSuffixLength, this.mergeCouponCodes); // sample result is "BNNXXX"
		},
	},

	watch: {
		dataECoupon: {
			deep: true,
			handler(data) {
				// Watch props data change and update this
				this.formData.newCouponCodes = data.newCouponCodes || [];
				this.formData.couponCodes = data.couponCodes || [];
			},
		},
	},

	methods: {
		...mapActions({
			showToast: 'toast/showToast',
		}),
		emitECouponData() {
			const onlyNewCouponCodes = this.isEditMode ? this.uniqueNewCouponCodes : [];

			this.$emit('update:ecoupon', {
				prefix: this.codePrefix,
				length: this.codeSuffixLength,
				quantity: this.totalCouponCodes,
				couponCodes: this.mergeCouponCodes, // Need to keep current pattern
				newCouponCodes: onlyNewCouponCodes,
			});
		},
		importCouponCode() {
			const fileSelector = document.createElement('input');
			fileSelector.setAttribute('type', 'file');
			fileSelector.setAttribute('accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel');
			fileSelector.onchange = async () => {
				const file = fileSelector.files.item(0);

				// UI ประกาศไว้ว่ารับไม่เกิน 10 MB — เดิมไม่เคยเช็คเลย ปล่อยไปตายที่ BE
				if (file && file.size > MAXIMUM_COUPON_IMPORT_FILE_SIZE) {
					this.showToast({
						content: `File size must not exceed ${MAXIMUM_COUPON_IMPORT_FILE_SIZE_LABEL}.`,
						header: 'Failure',
						type: 'danger',
					});

					return;
				}

				const params = {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				};

				this.isImporting = true;

				try {
					const res = await importCouponCodesAPI(file, params);
					// เดิมใช้ `[...res?.data?.data] || []` ซึ่ง throw ที่ spread ก่อน `||` จะได้ทำงาน
					const importedCodes = Array.isArray(res?.data?.data) ? [...res.data.data] : [];

					const nextCouponCodes = this.isEditMode ? this.formData.couponCodes : importedCodes;
					// import ซ้ำต้องสะสมต่อ ไม่ใช่ทับของรอบก่อน (ให้ตรงกับฝั่ง Auto)
					const nextNewCouponCodes = this.isEditMode
						? [...this.formData.newCouponCodes, ...importedCodes]
						: [];

					// ไม่จำกัดจำนวนโค้ดฝั่ง FE สำหรับ MANUAL (ธุรกิจต้อง import ได้ถึง ~250,000 โค้ดในรอบเดียว)
					// เพดาน 50,000 ของ MAXIMUM_COUPON_QUANTITY ใช้กับโหมด AUTO เท่านั้น
					this.formData.couponCodes = nextCouponCodes;
					this.formData.newCouponCodes = nextNewCouponCodes;

					// emit ทุกครั้งที่สำเร็จ แม้ผลลัพธ์จะว่าง เพื่อให้ state ฝั่ง parent ตรงกับที่แสดงอยู่เสมอ
					this.emitECouponData();

					this.showToast({
						content: 'Coupon codes successfully uploaded.',
						header: 'Success',
						type: 'success',
					});
				} catch (e) {
					this.showToast({
						content: 'Coupon codes upload failed',
						header: 'Failure',
						type: 'danger',
					});
				} finally {
					this.isImporting = false;
				}
			};
			fileSelector.click();
		},
	},
};
</script>

<style lang="scss" scoped>
	.coupon-codes {
		border-radius: rem(4);
		border: rem(1) solid $color-gray-400;
		background: $color-white;
	}
</style>
